import React, { useEffect, useState } from "react";
import { useLocalStorage } from "sprint-common-web";

import { useOrdersModuleApi } from "../../../common/useOrdersModuleApi";
import { getDefaultOrderRequestListingConfig, OrderRequestListingConfig } from "../config/OrderRequestListingConfig";
import { OrderRequestListingFilters } from "../hooks/useOrderRequestListingFilters";
import { mapListingResponseToOrderRequestListingItem, OrderRequestListingItem } from "../OrderRequestListingItem";
import { OrderRequestListingContext } from "./OrderRequestListingContext";

export type OrderRequestListingProviderProps = React.PropsWithChildren<unknown>;

const ORDER_REQUEST_LISTING_CONFIG_KEY = "orderRequestListingConfig";
const PINNED_ORDER_REQUESTS_KEY = "pinnedOrderRequests";

export const OrderRequestListingProvider: React.FC<OrderRequestListingProviderProps> = (
    props: OrderRequestListingProviderProps
) => {
    const { getOrderRequestList } = useOrdersModuleApi();

    const [items, setItems] = useState<OrderRequestListingItem[]>([]);
    const [selectedItems, setSelectedItems] = useState<OrderRequestListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [listingConfig, setListingConfig] = useLocalStorage<OrderRequestListingConfig>(
        ORDER_REQUEST_LISTING_CONFIG_KEY,
        getDefaultOrderRequestListingConfig()
    );

    const [pinnedItems, setPinnedItems] = useLocalStorage<string[]>(PINNED_ORDER_REQUESTS_KEY, []);

    useEffect(() => {
        setItems((prevItems) => {
            const pinned: OrderRequestListingItem[] = [];
            const unpinned: OrderRequestListingItem[] = [];

            prevItems.forEach((item) => {
                if (pinnedItems.includes(item.id)) {
                    pinned.push({ ...item, isPinned: true });
                } else {
                    unpinned.push({ ...item, isPinned: false });
                }
            });

            return [...pinned, ...unpinned];
        });
    }, [pinnedItems.length]);

    const reloadData = async (filters: OrderRequestListingFilters): Promise<void> => {
        setIsLoading(true);

        const response = await getOrderRequestList({
            page: filters.page,
            pageSize: filters.pageSize,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            statuses: filters.statuses,
            content: filters.content,
            pONumber: filters.pONumber,
            distributorId: filters.distributorId,
            systemIds: filters.systemIds,
            airlineIds: filters.airlineIds,
            filename: filters.filename,
            progresses: filters.progresses,
            sources: filters.sources,
            assetsAssociationStates: filters.assetsAssociationStates,
            dueDateStart: filters.dueDateStart?.toISOString(),
            dueDateEnd: filters.dueDateEnd?.toISOString(),
            requestIds: pinnedItems
        });

        const listingItems = response.items.map((item) =>
            mapListingResponseToOrderRequestListingItem(item, pinnedItems)
        );

        setItems(listingItems);
        setTotalItems(response.totalCount);
        setIsLoading(false);
    };

    const markAsChanged = (itemId: string) => {
        const newItems = [...items];
        const itemToUpdateIndex = newItems.findIndex((item) => item.id === itemId);

        newItems[itemToUpdateIndex].isModified = true;
        setItems(newItems);
    };

    const updateOrderRequest = (
        orderRequest: Partial<OrderRequestListingItem> & Required<Pick<OrderRequestListingItem, "id">>
    ) => {
        setItems((prevItems) =>
            prevItems.map((item) => (item.id === orderRequest.id ? { ...item, ...orderRequest } : item))
        );
    };

    const togglePinnedOrderRequest = (itemId: string) => {
        setPinnedItems((prevState) =>
            prevState.includes(itemId) ? prevState.filter((item) => item !== itemId) : [...prevState, itemId]
        );
    };

    return (
        <OrderRequestListingContext.Provider
            value={{
                items,
                selectedItems,
                pinnedItems,
                totalItems,
                isLoading,
                setSelectedItems,
                markAsChanged,
                updateOrderRequest,
                listingConfig,
                saveListingConfig: setListingConfig,
                reloadData,
                togglePinnedOrderRequest
            }}
        >
            {props.children}
        </OrderRequestListingContext.Provider>
    );
};
